<template>
    <v-menu right bottom offset-y nudge-bottom="23px" transition="slide-y-transition"
            min-width="400px" max-width="400px"
            max-height="calc(100vh - 88px)"
            content-class="menu-panel"
            :close-on-content-click="false"
            @input="setIsMenuOpened"
            :value="isMenuOpened"
    >
        <template v-slot:activator="{on, attrs}">
            <v-btn
                large plain class="rounded-lg"
                :outlined="isAlert"
                :class="{'events-btn': true, 'is-opened': isMenuOpened}"
                v-bind="attrs" v-on="on"
            >
                <v-badge v-if="hasActual"
                    bottom color="warning" dot
                    :offset-x="8"
                    :offset-y="8"
                     bordered
                >
                    <v-icon>mdi-bell</v-icon>
                </v-badge>
                <v-icon v-if="!hasActual">mdi-bell</v-icon>
                {{ isAlert ? '&nbsp;Тревога' : '&nbsp;События' }}
            </v-btn>
        </template>

        <v-container fluid class="panel-heading">
            <h3>События</h3>
            <Spinner v-if="this.isLoading" style="margin-left: 8px;" />
            <v-spacer />

            <router-link
                to="/events" @click.native="hideMenu"
                class="clean-link" style="margin-right: 12px;"
            >
                Все события
            </router-link>

            <v-tooltip bottom>
                <template v-slot:activator="{on, attrs}">
                    <v-btn icon @click="areOnlyActualShown = !areOnlyActualShown" v-bind="attrs" v-on="on">
                        <v-icon :color="areOnlyActualShown ? 'primary' : ''">
                            mdi-bell-badge-outline
                        </v-icon>
                    </v-btn>
                </template>
                <span>Показывать только актуальные</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{on, attrs}">
                    <v-btn icon @click="isSoundEnabled = !isSoundEnabled" v-bind="attrs" v-on="on">
                        <v-icon :color="isSoundEnabled ? 'primary' : ''">
                            {{ isSoundEnabled ? 'mdi-volume-high' : 'mdi-volume-off' }}
                        </v-icon>
                    </v-btn>
                </template>
                <span>Включить звук</span>
            </v-tooltip>
        </v-container>

        <v-container fluid class="pa-4 pt-0" style="flex-grow: 1; flex-shrink: 1; overflow-y: auto;">
            <p v-if="shownEvents.length === 0" class="text-center mt-8">
                Нет {{ areOnlyActualShown ? 'актуальных' : '' }} событий
            </p>
            <EventCard v-for="event in shownEvents" :key="event.id" :value="event" @hide-menu="hideMenu" />
        </v-container>

        <audio id="alertAudio" src="/alert.mp3" preload="auto" />
    </v-menu>
</template>


<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import {Inject, Watch} from 'vue-property-decorator'
import Api from '@/services/Api'
import {Event, LEVEL_ALERT} from '@/models/Event'
import EventCard from '@/components/app-bar/EventCard.vue'
import Spinner from '@/components/Spinner.vue'

@Component({
    components: {Spinner, EventCard},
})
export default class EventsWidget extends Vue
{
    @Inject('api') private api!: Api

    fetchInterval = 2000
    emptyAudio = new Audio('/empty.mp3')
    alertAudio = new Audio('/alert.mp3')

    events: Event[] = []
    isAlert = false
    isMenuOpened = false

    updateIntervalId: ReturnType<typeof setInterval> | null = null
    isLoading = false

    get isSoundEnabled() {
        return this.$store.state.settings?.appBar?.events?.isSoundEnabled ?? false
    }

    set isSoundEnabled(value: boolean) {
        if (value) {
            this.requestAudioPermission()
        }
        this.$store.commit('setSetting', ['appBar.events.isSoundEnabled', value])
    }

    get areOnlyActualShown() {
        return this.$store.state.settings?.appBar?.events?.areOnlyActualShown ?? true
    }

    set areOnlyActualShown(value: boolean) {
        if (value) {
            this.requestAudioPermission()
        }
        this.$store.commit('setSetting', ['appBar.events.areOnlyActualShown', value])
        this.fetchEvents()
    }

    get isAlertPlaying() {
        return this.isSoundEnabled && this.isAlert
    }

    created() {
        this.updateIntervalId = setInterval(() => this.fetchEvents(), this.fetchInterval)
        this.fetchEvents()
    }

    destroyed() {
        if (this.updateIntervalId) {
            clearInterval(this.updateIntervalId)
            this.updateIntervalId = null
        }
    }

    async fetchEvents() {
        if (this.isLoading) {
            return
        }

        this.isLoading = true
        this.events = await this.api.listEvents({
            isActual: this.areOnlyActualShown,
            expand: ['levelLabel', 'typeLabel', 'device.name', 'extra'],
            limit: 20,
        })
        this.isLoading = false
    }

    @Watch('isAlert')
    onIsAlertChange(isAlert: boolean) {
        this.$emit('alert-change', isAlert)
    }

    get shownEvents() {
        return this.areOnlyActualShown ? this.events.filter(e => e.isActual) : [...this.events]
    }

    @Watch('events')
    onEventsChange(events: Event[]) {
        if (events.some(e => e.isActual && e.level != null && e.level >= LEVEL_ALERT)) {
            this.isAlert = true
            return
        }
        this.isAlert = false
    }

    setIsMenuOpened(isOpened: boolean) {
        this.isMenuOpened = isOpened
    }

    get hasActual() {
        return this.events.some(e => e.isActual)
    }

    @Watch('isAlertPlaying')
    onIsAlertPlayingChange(isAlertPlaying: boolean) {
        if (isAlertPlaying) {
            this.alertAudio.loop = true
            this.alertAudio.currentTime = 0
            this.alertAudio.play()
        } else {
            this.alertAudio.pause()
        }
    }

    requestAudioPermission() {
        const promiseMaybe = this.emptyAudio.play()
        if (promiseMaybe && promiseMaybe.catch) {
            promiseMaybe.catch(ex => {
                console.log(ex.toString())
            })
        }
    }

    hideMenu() {
        this.isMenuOpened = false
    }
}
</script>
